import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Mail, KeyRound, MessageSquare } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, DevCredit } from "@/components/landing/Logo";

const KINDS = [["message", "General message", MessageSquare], ["request", "Feature / support request", Mail], ["license", "License request", KeyRound]];

export default function Contact() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", kind: new URLSearchParams(window.location.search).get("kind") || "message", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/contact", f); setSent(true); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const input = "neu-inset w-full rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#3D3DF0]/30";

  return (
    <div className="min-h-screen bg-[#EEF2F8]" data-testid="contact-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
        <Link to="/"><Logo /></Link>
        <Link to={user ? "/dashboard" : "/"} className="neu-btn rounded-full px-5 py-2 text-sm font-semibold" data-testid="contact-back-btn">{user ? "Library" : "Home"}</Link>
      </header>
      <main className="mx-auto grid max-w-6xl gap-12 px-6 pb-20 pt-8 md:px-10 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-widest text-[#A62B8A]">Contact</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Talk to the <span className="sg-text-gradient">iBookStudio</span> team.</h1>
          <p className="mt-4 text-[#64748B]">Questions, feature ideas, or a license upgrade (1, 3, 6 or 12 months)? Send us a note and the admin will get back to you by email.</p>
          <p className="mt-6 font-notebook text-lg text-[#A62B8A]">Every new account starts with a 7-day free trial.</p>
        </div>
        <div className="neu-panel rounded-[32px] p-8 lg:col-span-3">
          {sent ? (
            <div className="py-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-[#5FA82E]" />
              <p className="mt-4 font-display text-2xl font-bold">Message sent!</p>
              <p className="mt-1 text-sm text-[#64748B]">We’ll reply to {f.email} soon.</p>
              <button onClick={() => { setSent(false); setF({ ...f, subject: "", message: "" }); }} className="neu-btn mt-6 rounded-full px-5 py-2 text-sm font-semibold" data-testid="contact-send-another">Send another</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {KINDS.map(([k, l, I]) => (
                  <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} data-testid={`contact-kind-${k}`} className={`flex items-center gap-2 rounded-2xl px-3 py-3 text-xs font-semibold ${f.kind === k ? "neu-pressed text-[#3D3DF0]" : "neu-btn"}`}><I className="h-4 w-4" />{l}</button>
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={f.name} onChange={set("name")} placeholder="Your name" className={input} data-testid="contact-name" />
                <input required type="email" value={f.email} onChange={set("email")} placeholder="you@example.com" className={input} data-testid="contact-email" />
              </div>
              <input value={f.subject} onChange={set("subject")} placeholder="Subject" className={input} data-testid="contact-subject" />
              <textarea required rows={6} value={f.message} onChange={set("message")} placeholder="How can we help?" className={input} data-testid="contact-message" />
              <button disabled={busy} className="neu-btn-primary flex items-center gap-2 rounded-full px-7 py-3 font-bold disabled:opacity-60" data-testid="contact-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send message</button>
            </form>
          )}
        </div>
      </main>
      <DevCredit className="pb-8 pt-4" />
    </div>
  );
}
